import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { SignedIn, SignedOut, SignIn, useAuth } from '@clerk/clerk-react';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Analytics from './pages/Analytics';
import Support from './pages/Support';
import Optimization from './pages/Optimization';
import { setTokenGetter } from './api/trpc';
import { setAuthenticated } from './api/client';

/**
 * Component to set up Clerk token for tRPC
 */
function ClerkTokenProvider({ children }: { children: React.ReactNode }) {
  const { getToken, isSignedIn } = useAuth();

  useEffect(() => {
    setTokenGetter(getToken);
    setAuthenticated(!!isSignedIn);
  }, [getToken, isSignedIn]);

  return <>{children}</>;
}

/**
 * Login page component
 */
function LoginPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
      <div className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Portal Admin
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Sign in to access the admin dashboard
          </p>
        </div>
        <SignIn
          appearance={{
            elements: {
              rootBox: 'w-full',
              card: 'bg-white dark:bg-slate-800 rounded-xl shadow-lg',
            },
          }}
          fallbackRedirectUrl="/dashboard"
        />
      </div>
    </div>
  );
}

function App() {
  return (
    <>
      <SignedOut>
        <LoginPage />
      </SignedOut>

      <SignedIn>
        <ClerkTokenProvider>
          <div className="flex min-h-screen bg-gray-50 dark:bg-slate-900">
            <Sidebar />
            <main className="flex-1 ml-64 p-8">
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/users" element={<Users />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/support" element={<Support />} />
                <Route path="/optimization" element={<Optimization />} />
              </Routes>
            </main>
          </div>
        </ClerkTokenProvider>
      </SignedIn>
    </>
  );
}

export default App;
